import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import Carousel from 'react-multi-carousel';
import { Overlay, Popover, PopoverContent } from 'react-bootstrap';

import LazyImage from '../../ui-core/Image';
import ModalBox from '../../ui-core/Modal';
import { IMAGE_CONSTANT } from '../../constants/app';
import { doPlaybackHistoryRequest } from '../../store/action/common';
import { doMyPlansRequest } from '../../store/action/user';
import {
  doActivePlansRequest,
  doPlanReset,
  doPlanRemove,
} from '../../store/action/plan';
import {
  getCommunityStatsRequest,
  getCommunityProfileExtendedRequest,
} from '../../api/community';
import { encodeURLParams } from '../../utils/url';
import { APP_ROUTES, getRoute } from '../../route';

import './SidebarSummary.css';

const getOrdinalSuffix = day => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const formatDayLabel = date => {
  if (date.isSame(dayjs(), 'day')) return 'Today';
  return `${date.date()}${getOrdinalSuffix(date.date())}`;
};

const buildWeekDays = activityDays => {
  const completedDates = new Set(
    (activityDays || []).map(d => dayjs(d).format('YYYY-MM-DD')),
  );

  return Array.from({ length: 7 }, (_, index) => {
    const date = dayjs().subtract(6 - index, 'day');
    const dateKey = date.format('YYYY-MM-DD');
    const isToday = date.isSame(dayjs(), 'day');
    const isCompleted = completedDates.has(dateKey);

    return {
      key: dateKey,
      label: formatDayLabel(date),
      isToday,
      isCompleted,
    };
  });
};

const getActivityDays = attributes => {
  if (!attributes) return [];
  const activity = attributes.activity || attributes.activity_data;
  if (Array.isArray(activity?.days)) return activity.days;
  if (Array.isArray(attributes.activity_days)) return attributes.activity_days;
  if (Array.isArray(attributes.weekly_activity))
    return attributes.weekly_activity;
  return [];
};

const getStreakWeeks = attributes => {
  if (!attributes) return 0;
  const activity = attributes.activity || attributes.activity_data;
  return (
    activity?.week_streak ??
    activity?.streak_weeks ??
    attributes.week_streak ??
    attributes.streak_weeks ??
    attributes.activity_streak ??
    0
  );
};

const getStatValue = (attributes, keys, fallback) => {
  if (!attributes) return fallback;
  const stats = attributes.stats || attributes.profile_stats;
  for (const key of keys) {
    const value = stats?.[key] ?? attributes[key];
    if (value !== undefined && value !== null) return value;
  }
  return fallback;
};

const SidebarActivePlanCard = ({
  plan,
  onPlanClick,
  onResetClick,
  onRemoveClick,
}) => {
  const { attributes } = plan;
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const phasePercentage = attributes?.phase_percentage || 0;

  const handleMenuToggle = event => {
    event.stopPropagation();
    setShowMenu(prev => !prev);
  };

  return (
    <div className="sidebar-active-plan-card" onClick={() => onPlanClick(plan)}>
      {phasePercentage > 0 && (
        <button
          type="button"
          className="sidebar-active-plan-menu-btn menu-icon menu-icon-dots"
          onClick={handleMenuToggle}
          ref={menuRef}
          aria-label="Plan options"
        >
          <span />
          <span />
          <span />
        </button>
      )}
      <Overlay
        show={showMenu}
        target={menuRef.current}
        placement="bottom-end"
        rootClose
        onHide={() => setShowMenu(false)}
        transition={false}
      >
        <Popover bsPrefix="folders-popover">
          <PopoverContent className="p-0">
            <div
              className="menu-item"
              onClick={e => {
                e.stopPropagation();
                onRemoveClick(plan);
                setShowMenu(false);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="none"
              >
                <path
                  d="M8.75 17.375C8.75 17.5408 8.68415 17.6997 8.56694 17.8169C8.44973 17.9342 8.29076 18 8.125 18H3.75C3.41848 18 3.10054 17.8683 2.86612 17.6339C2.6317 17.3995 2.5 17.0815 2.5 16.75V4.25C2.5 3.91848 2.6317 3.60054 2.86612 3.36612C3.10054 3.1317 3.41848 3 3.75 3H8.125C8.29076 3 8.44973 3.06585 8.56694 3.18306C8.68415 3.30027 8.75 3.45924 8.75 3.625C8.75 3.79076 8.68415 3.94973 8.56694 4.06694C8.44973 4.18415 8.29076 4.25 8.125 4.25H3.75V16.75H8.125C8.29076 16.75 8.44973 16.8158 8.56694 16.9331C8.68415 17.0503 8.75 17.2092 8.75 17.375ZM17.3172 10.0578L14.1922 6.93281C14.0749 6.81554 13.9159 6.74965 13.75 6.74965C13.5841 6.74965 13.4251 6.81554 13.3078 6.93281C13.1905 7.05009 13.1247 7.20915 13.1247 7.375C13.1247 7.54085 13.1905 7.69991 13.3078 7.81719L15.3664 9.875H8.125C7.95924 9.875 7.80027 9.94085 7.68306 10.0581C7.56585 10.1753 7.5 10.3342 7.5 10.5C7.5 10.6658 7.56585 10.8247 7.68306 10.9419C7.80027 11.0592 7.95924 11.125 8.125 11.125H15.3664L13.3078 13.1828C13.1905 13.3001 13.1247 13.4591 13.1247 13.625C13.1247 13.7909 13.1905 13.9499 13.3078 14.0672C13.4251 14.1845 13.5841 14.2503 13.75 14.2503C13.9159 14.2503 14.0749 14.1845 14.1922 14.0672L17.3172 10.9422C17.3753 10.8841 17.4214 10.8152 17.4529 10.7393C17.4843 10.6635 17.5005 10.5821 17.5005 10.5C17.5005 10.4179 17.4843 10.3365 17.4529 10.2607C17.4214 10.1848 17.3753 10.1159 17.3172 10.0578Z"
                  fill="#70A6FF"
                />
              </svg>
              <span>Exit Plan</span>
            </div>
            <div
              className="menu-item"
              onClick={e => {
                e.stopPropagation();
                onResetClick(plan);
                setShowMenu(false);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="none"
              >
                <path
                  d="M18.75 4.875V8.625C18.75 8.79076 18.6841 8.94973 18.5669 9.06694C18.4497 9.18415 18.2907 9.25 18.125 9.25H14.375C14.2092 9.25 14.0503 9.18415 13.933 9.06694C13.8158 8.94973 13.75 8.79076 13.75 8.625C13.75 8.45924 13.8158 8.30027 13.933 8.18306C14.0503 8.06585 14.2092 8 14.375 8H16.5156L14.4383 6.09687L14.4187 6.07812C13.55 5.20976 12.4447 4.61661 11.2409 4.37277C10.037 4.12893 8.78804 4.24522 7.6499 4.70711C6.51177 5.169 5.53496 5.956 4.8415 6.96981C4.14804 7.98362 3.76869 9.17929 3.75082 10.4075C3.73295 11.6356 4.07736 12.8418 4.74103 13.8754C5.40469 14.9089 6.35819 15.724 7.48241 16.2188C8.60663 16.7136 9.85172 16.8662 11.0622 16.6575C12.2726 16.4488 13.3947 15.888 14.2883 15.0453C14.4088 14.9314 14.5696 14.8699 14.7354 14.8745C14.9011 14.8792 15.0583 14.9494 15.1723 15.0699C15.2862 15.1904 15.3476 15.3512 15.343 15.517C15.3384 15.6828 15.2681 15.8399 15.1476 15.9539C13.7582 17.2717 11.915 18.0043 9.99999 18H9.89686C8.66853 17.9832 7.46314 17.6649 6.38663 17.0731C5.31011 16.4813 4.39544 15.6342 3.723 14.6061C3.05056 13.5781 2.64094 12.4006 2.53014 11.1772C2.41935 9.95371 2.61076 8.72179 3.08757 7.58965C3.56438 6.45752 4.31198 5.45983 5.26467 4.68429C6.21735 3.90875 7.34595 3.37909 8.55128 3.14187C9.7566 2.90465 11.0017 2.96713 12.1773 3.32382C13.3528 3.68051 14.4227 4.32048 15.293 5.1875L17.5 7.20312V4.875C17.5 4.70924 17.5658 4.55027 17.683 4.43306C17.8003 4.31585 17.9592 4.25 18.125 4.25C18.2907 4.25 18.4497 4.31585 18.5669 4.43306C18.6841 4.55027 18.75 4.70924 18.75 4.875Z"
                  fill="#70A6FF"
                />
              </svg>
              <span>Reset Plan</span>
            </div>
          </PopoverContent>
        </Popover>
      </Overlay>
      <LazyImage
        src={attributes?.thumbnail || IMAGE_CONSTANT.therapyCardImg}
        alt={attributes?.title || 'Active plan'}
        placeholderSrc={IMAGE_CONSTANT.therapyCardImg}
        errorSrc={IMAGE_CONSTANT.therapyCardImg}
        className="sidebar-active-plan-thumb"
      />
      <div className="sidebar-active-plan-content">
        <h4 className="sidebar-active-plan-title">{attributes?.title}</h4>
        {phasePercentage > 0 && (
          <>
            <div className="sidebar-active-plan-progress">
              <span style={{ width: `${phasePercentage}%` }} />
            </div>
            <p className="sidebar-active-plan-progress-text">
              {Math.round(phasePercentage)}% Completed
            </p>
          </>
        )}
      </div>
    </div>
  );
};

SidebarActivePlanCard.propTypes = {
  plan: PropTypes.object.isRequired,
  onPlanClick: PropTypes.func.isRequired,
  onResetClick: PropTypes.func.isRequired,
  onRemoveClick: PropTypes.func.isRequired,
};

const SidebarSummary = ({ history }) => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.user);
  const { activePlans: activePlansState, isPlansLoading } = useSelector(
    state => state.plan,
  );
  const [playbackCount, setPlaybackCount] = useState(0);
  const [showResetPrompt, setShowResetPrompt] = useState(false);
  const [showRemovePrompt, setShowRemovePrompt] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plansPeekGutter, setPlansPeekGutter] = useState(0);
  const [communityStats, setCommunityStats] = useState(null);
  const [communityProfile, setCommunityProfile] = useState(null);
  console.log(communityProfile);
  const plansCarouselRef = useRef(null);

  const attributes = user?.attributes;
  const { myPlans } = useSelector(state => state.user);

  useEffect(() => {
    dispatch(doActivePlansRequest());
    dispatch(doMyPlansRequest());
    dispatch(
      doPlaybackHistoryRequest({
        getAllData: true,
        callback: response => {
          setPlaybackCount(response?.playbackHistoryList?.length || 0);
        },
      }),
    );

    // Fetch community stats and profile
    getCommunityStatsRequest()
      .then(response => {
        setCommunityStats(response?.data);
      })
      .catch(error => {
        console.error('Failed to fetch community stats:', error);
      });

    getCommunityProfileExtendedRequest()
      .then(response => {
        setCommunityProfile(response?.data);
      })
      .catch(error => {
        console.error('Failed to fetch community profile:', error);
      });
  }, [dispatch]);

  const completedPlansCount = useMemo(
    () =>
      (myPlans || []).filter(plan => plan?.attributes?.status === 'completed')
        .length,
    [myPlans],
  );

  const videosWatched = useMemo(() => {
    // Priority: community stats > user attributes > playback count
    if (communityStats?.data?.videos_watched !== undefined) {
      return communityStats.data.videos_watched;
    }
    const fromAttributes = getStatValue(
      attributes,
      ['videos_watched', 'videos_watched_count', 'total_videos_watched'],
      null,
    );
    return fromAttributes ?? playbackCount;
  }, [communityStats, attributes, playbackCount]);

  const plansCompleted = useMemo(() => {
    // Priority: community stats > user attributes > completed plans count
    if (communityStats?.data?.plans_completed !== undefined) {
      return communityStats.data.plans_completed;
    }
    const fromAttributes = getStatValue(
      attributes,
      ['plans_completed', 'plans_completed_count', 'completed_plans'],
      null,
    );
    return fromAttributes ?? completedPlansCount;
  }, [communityStats, attributes, completedPlansCount]);

  const streakWeeks = useMemo(() => {
    if (communityStats?.data?.streak !== undefined) {
      return communityStats.data.streak;
    }
    return getStreakWeeks(attributes);
  }, [communityStats, attributes]);

  const weekDays = useMemo(() => {
    // Build week days from community stats daily_activity if available
    if (communityStats?.data?.daily_activity) {
      return communityStats.data.daily_activity.map(day => {
        const date = dayjs(day.date);
        const isToday = date.isSame(dayjs(), 'day');
        const isCompleted = day.watched;

        return {
          key: day.date,
          label: formatDayLabel(date),
          isToday,
          isCompleted,
        };
      });
    }
    return buildWeekDays(getActivityDays(attributes));
  }, [communityStats, attributes]);

  const activePlans = useMemo(() => {
    return (activePlansState?.data || []).filter(
      plan => plan?.attributes?.status !== 'not_started',
    );
  }, [activePlansState?.data]);

  useEffect(() => {
    const carouselEl = plansCarouselRef.current;
    if (!carouselEl) return undefined;

    const updatePeekGutter = () => {
      const width = carouselEl.offsetWidth;
      setPlansPeekGutter(width > 0 ? Math.round(width / 5) : 0);
    };

    updatePeekGutter();
    const resizeObserver = new ResizeObserver(updatePeekGutter);
    resizeObserver.observe(carouselEl);
    return () => resizeObserver.disconnect();
  }, [activePlans.length, isPlansLoading]);

  const sidebarActivePlansCarousel = useMemo(
    () => ({
      desktop: {
        breakpoint: { max: 3000, min: 0 },
        items: 1,
        partialVisibilityGutter: plansPeekGutter,
      },
    }),
    [plansPeekGutter],
  );

  const handlePlanClick = plan => {
    const queryParams = {
      card_color: plan.attributes.card_color,
      title: plan.attributes.title,
    };
    const options = {
      pathParam: { id: plan.id },
      searchParam: { params: encodeURLParams(queryParams) },
    };
    history.push(getRoute(APP_ROUTES.THERAPY_PLANS_DETAILS, options));
  };

  const handleReset = () => {
    dispatch(
      doPlanReset({
        planId: selectedPlan.id,
        callback: () => {
          setShowResetPrompt(false);
          dispatch(doActivePlansRequest());
        },
      }),
    );
  };

  const handleRemove = () => {
    dispatch(
      doPlanRemove({
        planId: selectedPlan.id,
        callback: () => {
          setShowRemovePrompt(false);
          dispatch(doActivePlansRequest());
        },
      }),
    );
  };

  const formatStatNumber = value => String(value ?? 0);

  return (
    <div className="profile-sidebar-summary">
      <section className="sidebar-summary-section">
        <div className="sidebar-summary-section-header">
          <h3 className="sidebar-summary-title">Activity</h3>
          <div className="sidebar-streak-badge">
            {streakWeeks > 0 && '🔥'}
            {streakWeeks === 0 && <img src={IMAGE_CONSTANT.streakImg} alt="" />}

            <span>{streakWeeks} Week Streak</span>
          </div>
        </div>
        <div className="sidebar-activity-week">
          {weekDays.map(day => (
            <div key={day.key} className="sidebar-activity-day">
              <div
                className={[
                  'sidebar-activity-circle',
                  day.isCompleted && 'completed',
                  day.isToday && !day.isCompleted && 'today',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {day.isCompleted && (
                  <svg viewBox="0 0 12 12" width="12" height="12" fill="none">
                    <path
                      d="M2 6l3 3 5-5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <span
                className={[
                  'sidebar-activity-day-label',
                  day.isToday && 'today',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {day.label}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="sidebar-summary-section">
        <h3 className="sidebar-summary-title">Active Plan</h3>
        {isPlansLoading ? (
          <div className="sidebar-active-plans-loading">
            <div className="sidebar-active-plan-skeleton" />
          </div>
        ) : activePlans.length ? (
          <div ref={plansCarouselRef} className="sidebar-active-plans-carousel">
            <Carousel
              responsive={sidebarActivePlansCarousel}
              className="sidebar-active-plans-list"
              partialVisible={activePlans.length > 1}
              showDots={activePlans.length > 1}
              arrows={false}
              swipeable
              draggable
              infinite={false}
              itemClass="sidebar-active-plans-carousel-item"
            >
              {activePlans.map(plan => (
                <SidebarActivePlanCard
                  key={plan.id}
                  plan={plan}
                  onPlanClick={handlePlanClick}
                  onResetClick={item => {
                    setSelectedPlan(item);
                    setShowResetPrompt(true);
                  }}
                  onRemoveClick={item => {
                    setSelectedPlan(item);
                    setShowRemovePrompt(true);
                  }}
                />
              ))}
            </Carousel>
          </div>
        ) : (
          <p className="sidebar-summary-empty">No active plans yet</p>
        )}
      </section>

      <div className="sidebar-stats-row">
        <div className="sidebar-stat-box videos">
          <span className="sidebar-stat-value">
            {formatStatNumber(videosWatched)}
          </span>
          <span className="sidebar-stat-label">Videos watched</span>
        </div>
        <div className="sidebar-stat-box plans">
          <span className="sidebar-stat-value">
            {formatStatNumber(plansCompleted)}
          </span>
          <span className="sidebar-stat-label">Plans completed</span>
        </div>
      </div>

      <ModalBox backdrop="static" show={showResetPrompt}>
        <div className="mt-2">
          <h5 className="reset-title">Reset plan</h5>
          <p className="reset-text">
            Are you sure you want to reset this plan? This action will
            permanently delete all progress data.
          </p>
          <div className="reset-btns">
            <button
              type="button"
              onClick={() => setShowResetPrompt(false)}
              className="w-50 btn cancel"
            >
              NOT NOW
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="w-50 btn yes-btn"
            >
              RESET PLAN
            </button>
          </div>
        </div>
      </ModalBox>
      <ModalBox backdrop="static" show={showRemovePrompt}>
        <div className="mt-2">
          <h5 className="reset-title">Remove Plan</h5>
          <p className="reset-text">
            Are you sure you want to remove this plan?
          </p>
          <div className="reset-btns">
            <button
              type="button"
              onClick={() => setShowRemovePrompt(false)}
              className="w-50 btn cancel"
            >
              NOT NOW
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="w-50 btn yes-btn"
            >
              REMOVE PLAN
            </button>
          </div>
        </div>
      </ModalBox>
    </div>
  );
};

SidebarSummary.propTypes = {
  history: PropTypes.object.isRequired,
};

export default SidebarSummary;
